import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  LayoutGrid,
  Rocket,
  AlertTriangle,
  XCircle,
  Zap,
  Map,
  Megaphone,
  Mail,
  Share2,
  Settings,
  BookOpen,
  Rss,
  Plus,
  ReceiptText,
  Sun,
  Moon,
  Check,
} from 'lucide-react';

/**
 * CommandPalette — global Cmd/Ctrl+K launcher for fast navigation, workspace
 * switching, and quick actions. Mounted once at the app root.
 */
export const CommandPalette = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { workspaces, currentWorkspaceId, selectWorkspace } = useWorkspace();
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const run = useCallback((fn) => {
    setOpen(false);
    // Defer so the dialog closes cleanly before navigation/state change
    setTimeout(fn, 0);
  }, []);

  const navItems = [
    { icon: LayoutGrid, label: 'All decisions', to: '/app', testId: 'cmd-all' },
    { icon: Rocket, label: 'Build Next', to: '/app?lane=build_next', testId: 'cmd-build' },
    { icon: AlertTriangle, label: 'Fix Now', to: '/app?lane=fix_now', testId: 'cmd-fix' },
    { icon: XCircle, label: 'Ignore', to: '/app?lane=ignore', testId: 'cmd-ignore' },
    { icon: Zap, label: 'Quick Wins', to: '/app?filter=quick_wins', testId: 'cmd-quickwins' },
    { icon: Plus, label: 'Add signals', to: '/import', testId: 'cmd-add-signals' },
    { icon: Rss, label: 'Signal Sources', to: '/sources', testId: 'cmd-sources' },
    { icon: Map, label: 'Roadmap', to: '/roadmap', testId: 'cmd-roadmap' },
    { icon: ReceiptText, label: 'Decision Receipts', to: '/receipts', testId: 'cmd-receipts' },
    { icon: Megaphone, label: 'Changelog', to: '/changelog', testId: 'cmd-changelog' },
    { icon: Mail, label: 'Weekly Digest', to: '/digest', testId: 'cmd-digest' },
    { icon: Share2, label: 'Share & Public URLs', to: '/share', testId: 'cmd-share' },
    { icon: Settings, label: 'Settings', to: '/settings/branding', testId: 'cmd-settings' },
    { icon: BookOpen, label: "Founder's Playbook", to: '/playbook', testId: 'cmd-playbook' },
  ];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search…" data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Navigate">
          {navItems.map((item) => (
            <CommandItem
              key={item.to}
              value={item.label}
              onSelect={() => run(() => navigate(item.to))}
              data-testid={item.testId}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Actions">
          <CommandItem
            value="toggle theme dark light mode"
            onSelect={() => run(toggleTheme)}
            data-testid="cmd-toggle-theme"
          >
            {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
            <span>Switch to {isDark ? 'light' : 'dark'} mode</span>
          </CommandItem>
        </CommandGroup>

        {workspaces && workspaces.length > 1 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Switch workspace">
              {workspaces.map((ws) => (
                <CommandItem
                  key={ws.id}
                  value={`workspace ${ws.name}`}
                  onSelect={() => run(() => selectWorkspace(ws.id))}
                  data-testid={`cmd-ws-${ws.id}`}
                >
                  <span className="mr-2 text-base leading-none">{ws.icon || '📦'}</span>
                  <span className="truncate">{ws.name}</span>
                  {ws.id === currentWorkspaceId && (
                    <Check className="ml-auto h-4 w-4 text-primary" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
